import { QrCode } from "@mui/icons-material"
import { AppBar, Box, Chip, Divider, Tooltip, Typography } from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { QRCodeGenerator } from "components/QRCodeGenerator/QRCodeGenerator"
import {
  DeviceDataGroup,
  DeviceEutGroup,
  Examination,
  File,
  MovementData,
} from "graphql/types"
import { useState } from "react"
import { getCurrentLocation } from "utils/getCurrentLocation"
import { getColorForStatus, getTextForStatus } from "utils/status"
import { DeliveryBillDialog } from "./DeliveryBillDialog"
import { EUTDataCollapsible } from "./EUTDataCollapsible"
import { EUTGaleryBox } from "./EUTGaleryBox"
import { EUTMovementDataDialog } from "./EUTMovementDataDialog"
import { EUTNotesCollapsible } from "./EUTNotesCollapsible"

type EUTManagmenentLeftBoxProps = {
  eut: DeviceEutGroup
  onSave: (arg: DeviceEutGroup) => void
  onCancel: () => void
  examinations: Examination[]
}

export const EUTManagementLeftBox = (props: EUTManagmenentLeftBoxProps) => {
  const { eut, onSave, onCancel, examinations } = props

  const [openDialog, setOpenDialog] = useState(false)
  const [openDeliveryBillDialog, setOpenDeliveryBillDialog] = useState(false)
  const [openQRGenerator, setOpenQRGenerator] = useState(false)

  const handleSaveMovementData = (value: MovementData, images: File[]) => {
    const newEut = {
      ...eut,
      images: [...eut.images, ...images],
      movementData: [...eut.movementData, value],
    }
    onSave(newEut)
  }

  const onSaveGroup = (arg: DeviceDataGroup): void => {
    const newEut = {
      ...eut,
      deviceDataGroup: eut.deviceDataGroup.map((group) =>
        group.id === arg.id ? arg : group
      ),
    }
    onSave(newEut)
  }

  return (
    <Box
      sx={{
        flex: 2,
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh-60px)",
      }}
    >
      {/* HEADER */}
      <AppBar
        position="static"
        sx={{
          padding: 1,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h6">
            {eut.barcode} {eut.name}
          </Typography>
          <Typography variant="subtitle1">
            Martin Meyer GmbH (787878)
          </Typography>
        </Box>
        <Box>
          <Typography>Letzte Aktualisierung: 19.12.1972</Typography>
          <Typography>
            Lagerplatz: {getCurrentLocation(eut.movementData)}
          </Typography>
        </Box>
      </AppBar>

      <Box
        sx={{
          width: "100%",
          height: "100vh",
          display: "flex",
          flexDirection: "row",
          overflow: "auto",
        }}
      >
        {/* MAIN LEFT*/}

        <Box
          sx={{
            flex: 4,
            padding: 1,
            paddingTop: 2,
            overflowY: "auto",
          }}
        >
          {eut.deviceDataGroup.map((group) => (
            <Box key={group.id} sx={{ mb: 2 }}>
              <EUTDataCollapsible
                group={group}
                onSave={onSaveGroup}
                isSaveDisabled={false}
                isCardActions={true}
              />
            </Box>
          ))}

          {/* Notes */}
          <Box>
            <EUTNotesCollapsible eut={eut} onSave={onSave} />
          </Box>
        </Box>

        {/* MAIN RIGHT*/}
        <Box
          sx={{
            flex: 3,
            padding: 1,
            paddingTop: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <EUTGaleryBox eut={eut} onSave={onSave} />

          <Box
            sx={{
              border: "1px solid #444",
              borderRadius: 1,
              padding: 1,
            }}
          >
            <Typography>Dokumente</Typography>
            <Divider sx={{ mb: 1 }} />
          </Box>

          <Box
            sx={{
              border: "1px solid #444",
              borderRadius: 1,
              padding: 1,
            }}
          >
            <Typography>Prüfungen</Typography>
            <Divider sx={{ mb: 1 }} />

            {examinations.map((item) => (
              <Box key={item.id} sx={{ display: "flex", gap: 1, mb: 1 }}>
                <Typography>{item.name} </Typography>
                <Chip
                  size="small"
                  label={getTextForStatus(item.status)}
                  color={getColorForStatus(item.status)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* FOOTER Navigation */}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
          position: "sticky",
          bottom: 0,
        }}
      >
        <Tooltip title="QR-Code generieren">
          <StyledButton
            buttonType="custom"
            customIcon={<QrCode />}
            onClick={() => setOpenQRGenerator(true)}
            variant="contained"
          />
        </Tooltip>

        <StyledButton
          buttonType="custom"
          customText="Logistik"
          onClick={() => setOpenDialog(true)}
          variant="contained"
        />

        <StyledButton
          buttonType="custom"
          customText="Lieferschein"
          onClick={() => setOpenDeliveryBillDialog(true)}
          variant="contained"
        />
        <StyledButton
          buttonType="custom"
          customText="Zurück"
          onClick={onCancel}
        />
      </Box>

      {/* MovementData - DIALOG */}
      <EUTMovementDataDialog
        openDialog={openDialog}
        onClose={() => setOpenDialog(false)}
        eut={eut}
        onSave={handleSaveMovementData}
        eutBarcode={[eut.barcode]}
      />

      <QRCodeGenerator
        open={openQRGenerator}
        onClose={() => setOpenQRGenerator(false)}
        barcode={eut.barcode}
        title={eut.name}
      />

      <DeliveryBillDialog
        open={openDeliveryBillDialog}
        onClose={() => setOpenDeliveryBillDialog(false)}
        eut={eut}
      />
    </Box>
  )
}
